import { cn } from "../../lib/utils"

export type SegmentedFilterTone = "accent" | "buy" | "sell"

export type SegmentedFilterOption<T extends string> = {
  label: string
  value: T
  activeTone?: SegmentedFilterTone
  disabled?: boolean
}

type SegmentedFilterProps<T extends string> = {
  value: T
  options: SegmentedFilterOption<T>[]
  onChange: (value: T) => void
  className?: string
  chipClassName?: string
  size?: "compact" | "regular"
  ariaLabel?: string
}

function activeToneClassName(tone: SegmentedFilterTone) {
  if (tone === "buy") return "text-success"
  if (tone === "sell") return "text-danger"
  return "text-foreground"
}

export function SegmentedFilter<T extends string>({
  value,
  options,
  onChange,
  className,
  chipClassName,
  size = "regular",
  ariaLabel,
}: SegmentedFilterProps<T>) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn(
        "flex min-w-0 items-center overflow-hidden rounded-[14px] border border-border-subtle bg-background-raised p-0.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]",
        className,
      )}
    >
      {options.map((option) => {
        const isActive = option.value === value
        const tone = option.activeTone ?? "accent"

        return (
          <button
            key={option.value}
            type="button"
            disabled={option.disabled}
            onClick={() => onChange(option.value)}
            aria-pressed={isActive}
            className={cn(
              "min-w-0 flex-1 rounded-[11px] font-display font-bold leading-none transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:cursor-not-allowed disabled:opacity-50",
              size === "compact" ? "min-h-8 px-2.5 py-1 text-xs" : "min-h-8 px-3 py-1.5 text-sm",
              isActive
                ? cn(
                    "bg-background-card shadow-[0_1px_0_rgba(255,255,255,0.08)]",
                    activeToneClassName(tone),
                  )
                : "text-foreground-faint hover:text-foreground",
              chipClassName,
            )}
          >
            <span className="block truncate">{option.label}</span>
          </button>
        )
      })}
    </div>
  )
}
